import type { ComponentCustomProperties, ObjectPlugin, Reactive, Ref } from 'vue';
import { getCurrentInstance, inject, reactive, ref } from 'vue';
import type { components as I18nComponents } from 'topvisor-openapi/src/ts/I18n';

export type Lang = 'ru' | 'en';

type Options = {
	/**
	 * Предзагруженные словари
	 *
	 * Для загрузки нужного словаря используйте @r/i18n.esm.js
	 *
	 * ```js
	 * import '@r/i18n.esm.js?Projects';
	 * ```
	 */
	i18n: I18nComponents['schemas'];

	/**
	 * Текущий язык
	 */
	lang: Lang;
};

/**
 * Загруженные словари
 *
 * Для загрузки нужного словаря используйте @r/i18n.esm.js
 *
 * ```js
 * import '@r/i18n.esm.js?Projects';
 * ```
 */
type I18n = Reactive<Options['i18n']>;

type I18nRender = <T extends keyof I18n>(categoryName: T, key: keyof I18n[T], props: Record<string, number | string>) => string;

// type i18nSetLang = (lang: Lang, forceReload?: boolean) => void;

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		/**
		 * Загруженные словари
		 *
		 * Можно использовать как функцию для подстановки значений в формате: `Текст {{ propName }}`
		 *
		 * @see I18n
		 * @see I18nRender
		 */
		$i18n: I18n & I18nRender;

		/**
		 * Текущий язык
		 */
		$i18nLang: Ref<Lang>;

		// /**
		//  * Установить язык
		//  */
		// $i18nSetLang: i18nSetLang;
	}
}

/**
 * Плагин работает в сваязке с плагином requirejs('i18n')
 *
 * При инициализации необходимо указать в $i18n объект с предзагруежнными словарями, остальные словари можно импортировать диномически вместе с
 * компонентами
 *
 * Внутри UI дозагрузка словарей недоступна и требует предзагрузки необходимых словарей
 *
 * Добавляет глобальные свойства:
 * - \$i18n
 * - \$i18nLang
 * - \$i18nL
 *
 * Для загрузки необходимого словаря указывайте его в зависимостях компонента:
 *
 * ```js
 * import '@r/i18n.esm.js?Projects';
 * ```
 *
 * В setup():
 *
 * ```js
 * useI18n().Projects.Site; // выполнит inject('$i18n').Projects.Site, не испоьзуйте inject('$i18n') напрямую, так как он не типизирован
 * ```
 *
 * В template:
 *
 * ```html
 * {{ $i18n.Projects.Site }}
 * ```
 *
 * С подстановкой значений:
 * ```html
 * 	{{ $i18n('Projects', 'Site', {name: value}) }}
 * 	```
 */
export default {

	install: (app, options) => {
		const i18nRender: I18nRender = (categoryName, key, props): string => {
			if (!app.config.globalProperties.$i18n[categoryName][key]) return '';

			let result = app.config.globalProperties.$i18n[categoryName][key] + '';

			// используется для быстрой работы словаря, вне словаря используйте `compile` в `@vue/compiler-dom`
			for (const name in props) {
				result = result.replaceAll(new RegExp(`{{ ${name} }}`, 'g'), String(props[name]));
			}

			return result;
		};

		Object.setPrototypeOf(i18nRender, reactive(options.i18n));

		app.config.globalProperties.$i18n = i18nRender as I18n & I18nRender;

		app.config.globalProperties.$i18nLang = ref(options.lang);
		// app.config.globalProperties.$i18nSetLang = () => {};

		/**
		 * @deprecated
		 */
		app.config.globalProperties.i18n = app.config.globalProperties.$i18n;

		// // это решение не работает, так как подмена значений в объекте не установит связь со словорем другого языка
		// // только в браузере (не ssr)
		// if (typeof window !== 'undefined' && window['requirejs']) {
		// 	app.config.globalProperties.$i18nSetLang = async (lang, forceReload) => {
		// 		await window['requirejs']('i18n').setLang(lang, forceReload);
		//
		// 		const categoriesActual = window['requirejs']('i18n').categories[lang];
		// 		Object.keys(categoriesActual).forEach(categoryName => {
		// 			app.config.globalProperties.$i18n[categoryName] = categoriesActual[categoryName];
		// 		});
		//
		// 		app.config.globalProperties.$i18nLang.value = lang;
		// 	};
		// }

		// так как плагина requirejs i18n нет в ui
		if (typeof window !== 'undefined' && !window['requirejs']) {
			globalThis.$i18n = app.config.globalProperties.$i18n;
		}

		app.provide('$i18n', app.config.globalProperties.$i18n);
		app.provide('$i18nLang', app.config.globalProperties.$i18nLang);
		// app.provide('$i18nSetLang', app.config.globalProperties.$i18nSetLang);

		/**
		 * @deprecated
		 */
		app.provide('i18n', app.config.globalProperties.i18n);
	},

} satisfies ObjectPlugin<Options>;

/**
 * Получить доступ к загруженным словарям
 *
 * @see I18n
 */
export const useI18n = (): ComponentCustomProperties['$i18n'] => {
	// @ts-ignore - requirejs i18n нет в ui
	if (!getCurrentInstance() && window['mo']?.user?.lang) return requirejs('i18n').categories[mo.user.lang];

	if (!getCurrentInstance() && globalThis.$i18n) return globalThis.$i18n;

	return inject('$i18n') as ComponentCustomProperties['$i18n'];
};

/**
 * Текущий язык
 */
export const useI18nLang = (): Ref<Lang> => {
	// @ts-ignore - no vue
	if (!getCurrentInstance() && window['mo']?.user?.lang) return ref(mo.user.lang);

	return inject('$i18nLang') as Ref<Lang>;
};

// /**
//  * Получить функцию для установки языка
//  */
// export const useI18nSetLang = () => {
// 	return inject('$i18nSetLang') as i18nSetLang;
// };
